import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Championship Calculator Component
 * Interactive "What If" scenarios for championship predictions
 * Calculate projected standings based on future race results
 */
const F1ChampionshipCalculator = ({ season = 2025 }) => {
  const [currentStandings, setCurrentStandings] = useState([]);
  const [remainingRaces, setRemainingRaces] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [projectedStandings, setProjectedStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 
  // Points system
  const POINTS_MAP = {
    1: 25, 2: 18, 3: 15, 4: 12, 5: 10,
    6: 8, 7: 6, 8: 4, 9: 2, 10: 1
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current driver standings
        const standingsResponse = await f1Service.getDriverStandings(season);
        if (!standingsResponse?.response) {
          throw new Error('No standings data available');
        }

        // Sort by points and take top 10
        const topDrivers = standingsResponse.response
          .sort((a, b) => (b.points || 0) - (a.points || 0))
          .slice(0, 10);

        setCurrentStandings(topDrivers);

        // Get remaining races
        const racesResponse = await f1Service.getCurrentSeasonRaces();
        const allRaces = racesResponse?.response || [];

        const now = new Date();
        const upcoming = allRaces.filter(r =>
          r.type === 'Race' &&
          new Date(r.date) > now
        );

        setRemainingRaces(upcoming);

        // Initialize predictions with "maintain position" strategy
        const initialPredictions = {};
        upcoming.forEach(race => {
          initialPredictions[race.id] = {};
          topDrivers.forEach((driver, index) => {
            initialPredictions[race.id][driver.driver.id] = index + 1; // Maintain current position
          });
        });
        setPredictions(initialPredictions);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching calculator data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [season]);

  // Calculate projected standings whenever predictions change
  useEffect(() => {
    if (currentStandings.length === 0) return;

    const projected = currentStandings.map(standing => {
      const driverId = standing.driver.id;
      let additionalPoints = 0;

      // Calculate points from remaining races
      remainingRaces.forEach(race => {
        const position = predictions[race.id]?.[driverId];
        if (position) {
          additionalPoints += POINTS_MAP[position] || 0;
        }
      });

      return {
        ...standing,
        currentPoints: standing.points || 0,
        projectedPoints: (standing.points || 0) + additionalPoints,
        additionalPoints
      };
    });

    // Sort by projected points
    const sorted = projected.sort((a, b) => b.projectedPoints - a.projectedPoints);
    setProjectedStandings(sorted);
  }, [predictions, currentStandings, remainingRaces]);

  // Update prediction for a driver in a specific race
  const updatePrediction = (raceId, driverId, position) => {
    setPredictions(prev => ({
      ...prev,
      [raceId]: {
        ...prev[raceId],
        [driverId]: parseInt(position)
      }
    }));
  };

  // Apply a scenario preset
  const applyScenario = (scenarioType) => {
    const newPredictions = {};

    remainingRaces.forEach(race => {
      newPredictions[race.id] = {};

      currentStandings.forEach((standing, index) => {
        const driverId = standing.driver.id;

        switch (scenarioType) {
          case 'leader-dominance':
            // Leader wins everything
            newPredictions[race.id][driverId] = index === 0 ? 1 : index + 1;
            break;

          case 'challenger-comeback':
            // Second place driver wins everything
            if (index === 0) {
              newPredictions[race.id][driverId] = 2; // Leader gets P2
            } else if (index === 1) {
              newPredictions[race.id][driverId] = 1; // Challenger wins
            } else {
              newPredictions[race.id][driverId] = index + 1;
            }
            break;

          case 'maintain':
            // Everyone maintains current championship position
            newPredictions[race.id][driverId] = index + 1;
            break;

          case 'chaos':
            // Random shake-up
            newPredictions[race.id][driverId] = Math.ceil(Math.random() * 10);
            break;

          default:
            newPredictions[race.id][driverId] = index + 1;
        }
      });
    });

    setPredictions(newPredictions);
  };

  // Get team color
  const getTeamColor = (teamName) => {
    const colors = f1Service.getTeamColors(teamName);
    return colors.primary;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-48 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4 space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-white/10 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Championship Calculator
          </h3>
          <span className="text-[10px] text-gray-600 font-mono uppercase tracking-wider">
            What If Scenarios
          </span>
        </div>

        {/* Explainer */}
        <div className="mb-4 p-3 bg-[#E10600]/5 border border-[#E10600]/20 rounded-sm">
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Predict the final championship standings by choosing how each remaining race finishes.
            Use preset scenarios or manually edit individual race results below.
          </p>
        </div>

        {/* Scenario Presets */}
        <div className="space-y-2">
          <span className="text-[10px] font-mono text-gray-600 uppercase tracking-wider">
            Quick Scenarios:
          </span>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => applyScenario('maintain')}
              className="px-3 py-2 text-[10px] font-mono bg-transparent text-gray-400 border border-white/10 hover:border-white/20 hover:text-white hover:bg-white/5 transition-all text-left"
            >
              <div className="font-bold uppercase tracking-wider mb-0.5">Maintain Positions</div>
              <div className="text-[9px] text-gray-600">Everyone keeps current ranking</div>
            </button>
            <button
              onClick={() => applyScenario('leader-dominance')}
              className="px-3 py-2 text-[10px] font-mono bg-transparent text-gray-400 border border-white/10 hover:border-white/20 hover:text-white hover:bg-white/5 transition-all text-left"
            >
              <div className="font-bold uppercase tracking-wider mb-0.5">Leader Dominance</div>
              <div className="text-[9px] text-gray-600">P1 wins every remaining race</div>
            </button>
            <button
              onClick={() => applyScenario('challenger-comeback')}
              className="px-3 py-2 text-[10px] font-mono bg-transparent text-gray-400 border border-white/10 hover:border-white/20 hover:text-white hover:bg-white/5 transition-all text-left"
            >
              <div className="font-bold uppercase tracking-wider mb-0.5">Challenger Comeback</div>
              <div className="text-[9px] text-gray-600">P2 wins everything to catch up</div>
            </button>
            <button
              onClick={() => applyScenario('chaos')}
              className="px-3 py-2 text-[10px] font-mono bg-transparent text-gray-400 border border-white/10 hover:border-white/20 hover:text-white hover:bg-white/5 transition-all text-left"
            >
              <div className="font-bold uppercase tracking-wider mb-0.5">Chaos Mode</div>
              <div className="text-[9px] text-gray-600">Random winners shake it up</div>
            </button>
          </div>
        </div>
      </div>

      {/* Info Bar */}
      <div className="p-4 border-b border-white/5 bg-white/[0.01]">
        <div className="flex items-center gap-6 text-xs font-mono">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">RACES REMAINING</span>
            <span className="text-white font-bold">{remainingRaces.length}</span>
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div className="flex items-center gap-2">
            <span className="text-gray-600">MAX POINTS AVAILABLE</span>
            <span className="text-white font-bold">{remainingRaces.length * 25}</span>
          </div>
        </div>
      </div>

      {/* Projected Standings */}
      <div className="p-4">
        <div className="text-[10px] text-gray-600 uppercase tracking-wider font-mono mb-3">
          Projected Final Standings
        </div>

        <div className="space-y-2">
          {projectedStandings.map((driver, index) => {
            const teamColor = getTeamColor(driver.team?.name);
            const isLeader = index === 0;
            const gap = isLeader ? 0 : projectedStandings[0].projectedPoints - driver.projectedPoints;

            return (
              <motion.div
                key={driver.driver.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05, duration: 0.2 }}
                className={`p-3 rounded-sm border ${
                  isLeader ? 'bg-[#E10600]/10 border-[#E10600]/20' : 'bg-white/[0.02] border-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Position */}
                  <div className="w-8 text-center">
                    <span className={`text-sm font-bold font-mono tabular-nums ${
                      isLeader ? 'text-[#E10600]' : 'text-gray-500'
                    }`}>
                      {index + 1}
                    </span>
                  </div>

                  {/* Team Color Bar */}
                  <div
                    className="w-1 h-10 rounded-full"
                    style={{ backgroundColor: teamColor }}
                  />

                  {/* Driver Info */}
                  <div className="flex-1">
                    <div className="text-sm font-bold text-white">
                      {driver.driver.name}
                    </div>
                    <div className="text-[10px] text-gray-500 font-mono">
                      {f1Service.getTeamCode(driver.team?.name)}
                    </div>
                  </div>

                  {/* Points Breakdown */}
                  <div className="text-right">
                    <div className="text-lg font-bold text-white font-mono tabular-nums">
                      {driver.projectedPoints}
                    </div>
                    <div className="text-[10px] text-gray-500 font-mono">
                      {driver.currentPoints}
                      {driver.additionalPoints > 0 && (
                        <span className="text-green-500"> +{driver.additionalPoints}</span>
                      )}
                    </div>
                  </div>

                  {/* Gap to Leader */}
                  {!isLeader && (
                    <div className="w-16 text-right">
                      <div className="text-xs text-gray-500 font-mono tabular-nums">
                        -{gap}
                      </div>
                    </div>
                  )}

                  {isLeader && (
                    <div className="w-16 text-right">
                      <div className="text-[9px] text-[#E10600] font-bold uppercase tracking-wider">
                        Leader
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Race Predictions (Expanded by default) */}
      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold text-white uppercase tracking-wider font-mono mb-1">
              Fine-Tune Race Results
            </div>
            <div className="text-[10px] text-gray-600">
              Manually adjust predictions for each remaining race
            </div>
          </div>
        </div>

        <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
          {remainingRaces.map((race, raceIndex) => (
            <div key={race.id} className="p-3 bg-white/[0.02] border border-white/5 rounded-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-bold text-white">
                  {race.competition?.name || `Race ${raceIndex + 1}`}
                </div>
                <div className="text-[9px] text-gray-600 font-mono">
                  {new Date(race.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              </div>

              <div className="space-y-2">
                {currentStandings.slice(0, 5).map((standing) => {
                  const driverId = standing.driver.id;
                  const currentPrediction = predictions[race.id]?.[driverId] || 1;

                  return (
                    <div key={driverId} className="flex items-center gap-2">
                      <div className="w-24 text-[10px] text-gray-400 truncate">
                        {standing.driver.name}
                      </div>
                      <select
                        value={currentPrediction}
                        onChange={(e) => updatePrediction(race.id, driverId, e.target.value)}
                        className="flex-1 bg-white/5 border border-white/10 text-white text-xs font-mono p-1 rounded focus:outline-none focus:border-white/20 hover:border-white/30 transition-colors"
                      >
                        {[...Array(20)].map((_, i) => (
                          <option key={i + 1} value={i + 1} className="bg-black">
                            P{i + 1}
                          </option>
                        ))}
                      </select>
                      <div className="w-12 text-right text-[10px] text-gray-600 font-mono">
                        +{POINTS_MAP[currentPrediction] || 0}pts
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="text-[10px] text-gray-600 font-mono uppercase tracking-wider text-center">
          Interactive Championship Projection
        </div>
      </div>
    </div>
  );
};

export default F1ChampionshipCalculator;
